/**
 * Q3-style HUD: big health/armor/ammo, weapon bar, crosshair, frag messages,
 * kill feed, damage flash, scoreboard (TAB / hold), touch controls, main menu.
 * Pure DOM — crisp on every DPI, zero GPU cost.
 */

import { WEAPONS, WEAPON_ORDER, WeaponId } from './weapons';
import { ArenaJoin } from '../core/arena';
import { LeaderboardEntry, LeaderboardPeriod } from '../core/leaderboard';
import { NICK_MAX, filterNicknameInput, loadNickname, saveNickname } from '../core/nickname';

export interface ScoreRow { name: string; frags: number; deaths: number; sats: number; me?: boolean; color: string; legend?: string }

/**
 * Échappement pour les rares insertions en innerHTML : le pseudo du joueur vient
 * du localStorage, donc d'une source modifiable à la main.
 */
function esc(s: string): string {
  return s.replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]!);
}

/** Seuil unique de « santé basse » (vignette rouge + chiffre orange). */
const LOW_HP = 25;

export class HUD {
  private root: HTMLDivElement;
  private els: Record<string, HTMLElement> = {};
  private killfeed: HTMLDivElement;
  private fragMsg: HTMLDivElement;
  private flash: HTMLDivElement;
  private scoreboard: HTMLDivElement;
  private menu: HTMLDivElement;
  private hitmarkerT = 0;
  private speedVal!: HTMLElement;
  /** dernières valeurs poussées dans le DOM (évite un reflow par frame) */
  private last = {
    health: -1, armor: -1, ammo: -1, sats: -1, frags: -1, ups: -1,
    weapon: '' as WeaponId | '', lead: '', quad: false,
    barWeapon: '' as WeaponId | '', barMask: -1,
  };
  /** un seul arc directionnel réutilisé (au lieu d'un élément DOM par coup) */
  private dmgArcs: HTMLElement[] = [];
  private dmgArcAt = 0;
  private xhair!: HTMLElement;
  private xhairWeapon: WeaponId | null = null;
  private spread = 6;
  private aimEnemy = false;
  private zooming = false;
  private lowHp = false;
  public onStart: ((legendId: string, name: string) => void) | null = null;
  public onJoinArena: (() => void) | null = null;
  public onWithdraw: (() => void) | null = null;
  /** annulation explicite du paiement d'entrée → le jeu purge la session stockée */
  public onCancelJoin: (() => void) | null = null;
  /** retour au menu de sélection sans recharger la page (session préservée) */
  public onBackToMenu: (() => void) | null = null;
  /** source du classement (null = hors ligne : l'écran l'annonce proprement) */
  public onLoadLeaderboard: ((period: LeaderboardPeriod) => Promise<LeaderboardEntry[]>) | null = null;
  public onLoadMyRank: ((period: LeaderboardPeriod) => Promise<number>) | null = null;
  private lbPeriod: LeaderboardPeriod = 'alltime';
  private lbReq = 0;
  private lbPlayerId: string | null = null;
  private lbOnline = true;
  private arenaQrUrl: string | null = null;
  public pollStop: (() => void) | null = null;
  /**
   * Pseudo courant. Conservé en champ (et non lu dans le DOM au démarrage du
   * match) : après paiement le menu a été remplacé par le panneau d'invoice,
   * l'input n'existe plus quand `startFight()` est appelé.
   */
  private nickname = loadNickname();

  // touch
  public touch = { moveX: 0, moveY: 0, lookDX: 0, lookDY: 0, fire: false, jump: false, zoom: false, active: false };
  private joyId = -1; private lookId = -1;
  private zoomBtn: HTMLElement | null = null;
  private joyOrigin = { x: 0, y: 0 }; private lookLast = { x: 0, y: 0 };

  constructor() {
    this.injectStyles();
    this.root = document.createElement('div');
    this.root.id = 'q3hud';
    this.root.innerHTML = `
      <div class="xhair" id="hud-xhair">
        <div class="ring"></div>
        <div class="tick n"></div><div class="tick s"></div><div class="tick e"></div><div class="tick w"></div>
        <div class="cross v"></div><div class="cross h"></div>
        <div class="dot"></div>
        <div class="hm"><i class="a"></i><i class="b"></i><i class="c"></i><i class="d"></i></div>
        <div class="killring"></div>
      </div>
      <div class="scope" id="hud-scope"><div class="sv"></div><div class="sh"></div></div>
      <div class="dmgdir" id="hud-dmgdir"></div>
      <div class="speed" id="hud-speed"><span class="v">0</span> <small>ups</small></div>
      <div class="bottom">
        <div class="stat health"><div class="ico">✚</div><div class="num" id="hud-health">100</div></div>
        <div class="weaponbar" id="hud-weapons"></div>
        <div class="stat armor"><div class="ico">◈</div><div class="num" id="hud-armor">0</div></div>
      </div>
      <div class="ammo"><div class="num" id="hud-ammo">120</div><div class="lbl" id="hud-weapon">LIGHTNING GUN</div></div>
      <div class="topleft">
        <div class="row"><span class="lbl">⚡ SATS</span><span class="val sat" id="hud-sats">0</span></div>
        <div class="row"><span class="lbl">FRAGS</span><span class="val" id="hud-frags">0</span></div>
        <div class="row"><span class="lbl">LEAD</span><span class="val" id="hud-lead">—</span></div>
      </div>
      <div class="topright">
        <div class="row"><span class="lbl">TIME</span><span class="val" id="hud-time">10:00</span></div>
        <div class="row"><span class="lbl">FRAG LIMIT</span><span class="val">20</span></div>
        <div class="row"><span class="lbl" id="hud-net">● OFFLINE</span></div>
      </div>
      <div class="quad" id="hud-quad">QUAD DAMAGE</div>
    `;
    document.body.appendChild(this.root);

    this.killfeed = document.createElement('div'); this.killfeed.className = 'killfeed'; this.root.appendChild(this.killfeed);
    this.fragMsg = document.createElement('div'); this.fragMsg.className = 'fragmsg'; this.root.appendChild(this.fragMsg);
    this.flash = document.createElement('div'); this.flash.className = 'dmgflash'; this.root.appendChild(this.flash);
    this.scoreboard = document.createElement('div'); this.scoreboard.className = 'scoreboard'; this.root.appendChild(this.scoreboard);
    this.menu = document.createElement('div'); this.menu.className = 'menu'; this.root.appendChild(this.menu);

    for (const id of ['hud-health', 'hud-armor', 'hud-ammo', 'hud-weapon', 'hud-weapons', 'hud-sats', 'hud-frags', 'hud-lead', 'hud-time', 'hud-net', 'hud-speed', 'hud-quad', 'hud-scope', 'hud-dmgdir']) {
      this.els[id] = document.getElementById(id)!;
    }
    this.xhair = document.getElementById('hud-xhair')!;
    this.speedVal = this.els['hud-speed'].querySelector('.v')!;
    this.setCrosshair('lightning');
    this.buildWeaponBar();
    this.setupTouch();
  }

  // ---------------------------------------------------------------- styles

  private injectStyles(): void {
    const s = document.createElement('style');
    s.textContent = `
      #q3hud { position: fixed; inset: 0; pointer-events: none; z-index: 900; font-family: 'Impact', 'Arial Narrow', ui-monospace, monospace; color: #fff; text-shadow: 0 2px 6px rgba(0,0,0,.9); user-select: none; -webkit-user-select: none; }
      /* --- réticule dynamique : --sp = écartement px, --c = couleur --- */
      #q3hud .xhair { position: absolute; top: 50%; left: 50%; width: 0; height: 0; --sp: 6px; --c: #eaf6ff; --sc: 1; transform: translate(-50%,-50%) scale(var(--sc)); transition: transform .09s ease-out, opacity .12s; }
      #q3hud .xhair .dot { position: absolute; left: 0; top: 0; width: 3px; height: 3px; margin: -1.5px; border-radius: 50%; background: var(--c); box-shadow: 0 0 6px var(--c), 0 0 2px #000; }
      #q3hud .xhair .tick { position: absolute; background: var(--c); box-shadow: 0 0 4px rgba(0,0,0,.9); opacity: 0; }
      #q3hud .xhair .tick.n, #q3hud .xhair .tick.s { width: 2px; height: 7px; left: -1px; }
      #q3hud .xhair .tick.e, #q3hud .xhair .tick.w { width: 7px; height: 2px; top: -1px; }
      #q3hud .xhair .tick.n { top: calc(-1 * var(--sp) - 7px); } #q3hud .xhair .tick.s { top: var(--sp); }
      #q3hud .xhair .tick.w { left: calc(-1 * var(--sp) - 7px); } #q3hud .xhair .tick.e { left: var(--sp); }
      #q3hud .xhair .cross { position: absolute; background: var(--c); opacity: 0; box-shadow: 0 0 4px rgba(0,0,0,.9); }
      #q3hud .xhair .cross.v { width: 1px; height: 46px; left: -.5px; top: -23px; }
      #q3hud .xhair .cross.h { width: 46px; height: 1px; top: -.5px; left: -23px; }
      #q3hud .xhair .ring { position: absolute; border: 1.5px solid var(--c); border-radius: 50%; opacity: 0; box-shadow: 0 0 5px rgba(0,0,0,.8); width: calc(var(--sp) * 2); height: calc(var(--sp) * 2); left: calc(-1 * var(--sp)); top: calc(-1 * var(--sp)); }
      /* variantes par arme */
      #q3hud .xhair.w-lightning .tick { opacity: .95; }
      #q3hud .xhair.w-rocket .ring { opacity: .8; } #q3hud .xhair.w-rocket .tick.n, #q3hud .xhair.w-rocket .tick.s { opacity: .6; }
      #q3hud .xhair.w-rail .cross { opacity: .85; } #q3hud .xhair.w-rail .tick { opacity: .5; }
      #q3hud .xhair.w-shotgun .ring { opacity: .7; border-style: dashed; } #q3hud .xhair.w-shotgun .tick { opacity: .35; }
      #q3hud .xhair.enemy { --c: #ff3a44; --sc: 1.18; }
      /* hitmarker gradué : 4 branches en X */
      #q3hud .xhair .hm { position: absolute; left: 0; top: 0; opacity: 0; transition: opacity .09s; --len: 8px; --hc: #fff; }
      #q3hud .xhair.hit .hm { opacity: 1; }
      #q3hud .xhair .hm i { position: absolute; width: 2px; height: var(--len); background: var(--hc); box-shadow: 0 0 6px var(--hc), 0 0 2px #000; transform-origin: 50% 0; }
      #q3hud .xhair .hm i.a { transform: rotate(45deg) translateY(5px); }
      #q3hud .xhair .hm i.b { transform: rotate(135deg) translateY(5px); }
      #q3hud .xhair .hm i.c { transform: rotate(225deg) translateY(5px); }
      #q3hud .xhair .hm i.d { transform: rotate(315deg) translateY(5px); }
      #q3hud .xhair .killring { position: absolute; left: -18px; top: -18px; width: 36px; height: 36px; border: 2px solid #f7931a; border-radius: 50%; opacity: 0; }
      #q3hud .xhair.kill .hm { --hc: #ffb84d; }
      #q3hud .xhair.kill .killring { animation: q3killring .45s ease-out forwards; }
      @keyframes q3killring { 0% { opacity: 1; transform: scale(.35); } 100% { opacity: 0; transform: scale(1.5); } }
      /* zoom / lunette */
      #q3hud .scope { position: absolute; inset: 0; opacity: 0; transition: opacity .12s; background: radial-gradient(circle at center, rgba(0,0,0,0) 26%, rgba(0,0,0,.55) 48%, rgba(0,0,0,.94) 72%); }
      #q3hud .scope.on { opacity: 1; }
      #q3hud .scope .sv { position: absolute; left: 50%; top: 0; bottom: 0; width: 1px; background: linear-gradient(to bottom, rgba(155,231,255,0), rgba(155,231,255,.35) 42%, rgba(155,231,255,0) 46%, rgba(155,231,255,0) 54%, rgba(155,231,255,.35) 58%, rgba(155,231,255,0)); }
      #q3hud .scope .sh { position: absolute; top: 50%; left: 0; right: 0; height: 1px; background: linear-gradient(to right, rgba(155,231,255,0), rgba(155,231,255,.35) 42%, rgba(155,231,255,0) 46%, rgba(155,231,255,0) 54%, rgba(155,231,255,.35) 58%, rgba(155,231,255,0)); }
      /* indicateur de dégâts directionnel */
      #q3hud .dmgdir { position: absolute; inset: 0; }
      #q3hud .dmgdir .arc { position: absolute; left: 50%; top: 50%; width: 220px; height: 220px; margin: -110px; border-radius: 50%; border: 5px solid transparent; border-top-color: rgba(255,50,60,.9); filter: drop-shadow(0 0 8px rgba(255,40,50,.8)); animation: q3dmgdir .55s ease-out forwards; }
      @keyframes q3dmgdir { 0% { opacity: 0; } 15% { opacity: 1; } 100% { opacity: 0; } }
      /* pulse vignette basse santé */
      #q3hud .dmgflash.lowhp { animation: q3lowhp .5s infinite; opacity: 1 !important; }
      @keyframes q3lowhp { 0%,100% { opacity: .12; } 50% { opacity: .45; } }
      #q3hud .speed { position: absolute; top: 58%; left: 50%; transform: translateX(-50%); font-size: 16px; opacity: .55; letter-spacing: 1px; font-family: ui-monospace, monospace; }
      #q3hud .speed small { font-size: 10px; }
      #q3hud .bottom { position: absolute; left: 0; right: 0; bottom: 18px; display: flex; align-items: flex-end; justify-content: center; gap: 26px; }
      #q3hud .stat { display: flex; align-items: center; gap: 8px; }
      #q3hud .stat .ico { font-size: 34px; opacity: .9; }
      #q3hud .stat .num { font-size: 58px; line-height: 1; letter-spacing: 1px; }
      #q3hud .health .num { color: #ffb84d; } #q3hud .health .ico { color: #ff5a5a; }
      #q3hud .health.low .num { color: #ff3344; animation: q3pulse .6s infinite; }
      #q3hud .armor .num { color: #ffd700; } #q3hud .armor .ico { color: #ffd700; }
      @keyframes q3pulse { 50% { opacity: .5; } }
      #q3hud .weaponbar { display: flex; gap: 6px; margin: 0 6px 10px; }
      #q3hud .weaponbar .w { width: 46px; height: 46px; border: 2px solid rgba(255,255,255,.25); border-radius: 6px; background: rgba(10,10,15,.6); display: flex; flex-direction: column; align-items: center; justify-content: center; font-size: 14px; opacity: .55; }
      #q3hud .weaponbar .w small { font-size: 10px; opacity: .8; font-family: ui-monospace, monospace; }
      #q3hud .weaponbar .w.sel { opacity: 1; border-color: #f7931a; box-shadow: 0 0 12px rgba(247,147,26,.6); background: rgba(247,147,26,.15); }
      #q3hud .weaponbar .w.empty { opacity: .25; }
      #q3hud .ammo { position: absolute; right: 26px; bottom: 90px; text-align: right; }
      #q3hud .ammo .num { font-size: 64px; line-height: 1; color: #9be7ff; }
      #q3hud .ammo .lbl { font-size: 14px; letter-spacing: 3px; opacity: .8; }
      #q3hud .topleft { position: absolute; top: 16px; left: 18px; font-family: ui-monospace, monospace; font-size: 14px; }
      #q3hud .topright { position: absolute; top: 16px; right: 18px; text-align: right; font-family: ui-monospace, monospace; font-size: 14px; }
      #q3hud .row { display: flex; gap: 10px; justify-content: space-between; min-width: 150px; margin: 2px 0; }
      #q3hud .topright .row { justify-content: flex-end; }
      #q3hud .lbl { opacity: .6; letter-spacing: 1px; font-size: 11px; }
      #q3hud .val { font-weight: 700; } #q3hud .sat { color: #f7931a; }
      #q3hud .quad { position: absolute; top: 12%; left: 50%; transform: translateX(-50%); font-size: 22px; letter-spacing: 6px; color: #66aaff; text-shadow: 0 0 18px #2266ff; display: none; }
      #q3hud .killfeed { position: absolute; top: 90px; right: 18px; display: flex; flex-direction: column; gap: 4px; align-items: flex-end; font-family: ui-monospace, monospace; font-size: 13px; }
      #q3hud .killfeed .k { background: rgba(10,10,15,.65); padding: 4px 10px; border-radius: 4px; border-left: 3px solid #f7931a; animation: q3fade 4s forwards; }
      #q3hud .killfeed .k b { color: #ffb84d; }
      @keyframes q3fade { 0%{opacity:0;transform:translateX(20px)} 8%{opacity:1;transform:none} 80%{opacity:1} 100%{opacity:0} }
      #q3hud .fragmsg { position: absolute; top: 22%; left: 50%; transform: translateX(-50%); font-size: 34px; letter-spacing: 2px; text-align: center; opacity: 0; transition: opacity .15s; white-space: nowrap; }
      #q3hud .fragmsg.show { opacity: 1; }
      #q3hud .fragmsg .sub { font-size: 18px; color: #f7931a; letter-spacing: 4px; margin-top: 4px; font-family: ui-monospace, monospace; }
      #q3hud .dmgflash { position: absolute; inset: 0; background: radial-gradient(ellipse at center, rgba(255,0,0,0) 40%, rgba(255,30,30,.55) 100%); opacity: 0; transition: opacity .08s; }
      #q3hud .scoreboard { position: absolute; top: 50%; left: 50%; transform: translate(-50%,-50%); min-width: 440px; background: rgba(6,6,12,.88); border: 1px solid rgba(247,147,26,.5); border-radius: 8px; padding: 14px 18px; display: none; font-family: ui-monospace, monospace; font-size: 14px; }
      #q3hud .scoreboard.show { display: block; }
      #q3hud .scoreboard h2 { margin: 0 0 10px; font-size: 20px; letter-spacing: 4px; color: #f7931a; text-align: center; font-family: Impact, 'Arial Narrow', sans-serif; }
      #q3hud .scoreboard table { width: 100%; border-collapse: collapse; }
      #q3hud .scoreboard th { text-align: left; opacity: .5; font-weight: normal; font-size: 11px; letter-spacing: 1px; padding: 4px 8px; }
      #q3hud .scoreboard td { padding: 5px 8px; border-top: 1px solid rgba(255,255,255,.06); }
      #q3hud .scoreboard tr.me td { background: rgba(247,147,26,.14); }
      #q3hud .scoreboard td.r { text-align: right; }
      #q3hud .scoreboard td .lg { opacity: .45; font-size: 11px; letter-spacing: 0; }
      #q3hud .menu { position: absolute; inset: 0; background: radial-gradient(ellipse at center, rgba(12,8,20,.75), rgba(3,2,8,.96)); display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 18px; pointer-events: auto; }
      #q3hud .menu.hide { display: none; }
      #q3hud .menu h1 { font-size: clamp(38px, 8vw, 84px); letter-spacing: 8px; margin: 0; color: #fff; text-shadow: 0 0 30px #f7931a, 0 0 60px rgba(247,147,26,.5); }
      #q3hud .menu h1 span { color: #f7931a; }
      #q3hud .menu .sub { font-family: ui-monospace, monospace; letter-spacing: 4px; opacity: .7; font-size: 13px; }
      #q3hud .menu .legends { display: grid; grid-template-columns: repeat(4, 1fr); gap: 8px; max-width: 640px; width: 92vw; }
      #q3hud .menu .lg { background: rgba(20,20,30,.8); border: 2px solid rgba(255,255,255,.12); border-radius: 8px; padding: 10px 8px; cursor: pointer; text-align: center; font-family: ui-monospace, monospace; font-size: 12px; transition: all .12s; }
      #q3hud .menu .lg:hover, #q3hud .menu .lg.sel { border-color: var(--c); box-shadow: 0 0 16px var(--c); transform: translateY(-2px); }
      #q3hud .menu .lg .sw { width: 100%; height: 6px; border-radius: 3px; background: var(--c); margin-bottom: 8px; }
      #q3hud .menu .lg .t { opacity: .55; font-size: 10px; }
      /* saisie du pseudo : 16px mini pour éviter le zoom auto iOS */
      #q3hud .menu .nick { display: flex; align-items: center; gap: 10px; background: rgba(20,20,30,.8); border: 2px solid rgba(247,147,26,.35); border-radius: 8px; padding: 8px 14px; width: min(420px, 88vw); transition: border-color .12s, box-shadow .12s; }
      #q3hud .menu .nick:focus-within { border-color: #f7931a; box-shadow: 0 0 16px rgba(247,147,26,.35); }
      #q3hud .menu .nick label { font-family: ui-monospace, monospace; font-size: 11px; letter-spacing: 2px; opacity: .6; white-space: nowrap; }
      #q3hud .menu .nick input { flex: 1; min-width: 0; font: 16px/1.2 ui-monospace, monospace; letter-spacing: 2px; text-transform: uppercase; padding: 6px 2px; border: 0; background: transparent; color: #ffb84d; outline: none; }
      #q3hud .menu .nick input::placeholder { color: rgba(255,255,255,.28); letter-spacing: 2px; }
      #q3hud .menu .nick .cnt { font-family: ui-monospace, monospace; font-size: 10px; opacity: .4; min-width: 34px; text-align: right; }
      #q3hud .menu button { font: inherit; font-family: Impact, 'Arial Narrow', sans-serif; font-size: 28px; letter-spacing: 6px; padding: 12px 44px; background: #f7931a; color: #0a0a0f; border: 0; border-radius: 6px; cursor: pointer; box-shadow: 0 0 30px rgba(247,147,26,.6); }
      #q3hud .menu button:hover { background: #ffb84d; }
      #q3hud .menu button.pay { background: transparent; color: #f7931a; border: 2px solid #f7931a; box-shadow: 0 0 20px rgba(247,147,26,.35); font-size: 20px; }
      #q3hud .menu button.pay:hover { background: rgba(247,147,26,.15); }
      #q3hud .menu .qr { width: min(420px, 84vw); height: min(420px, 84vw); border-radius: 10px; background: #fff; padding: 8px; }
      #q3hud .menu .invoice { font-family: ui-monospace, monospace; font-size: 11px; max-width: 560px; width: 90vw; word-break: break-all; background: rgba(255,255,255,.05); border: 1px solid rgba(255,255,255,.12); border-radius: 6px; padding: 10px 14px; opacity: .85; }
      #q3hud .menu .btnrow { display: flex; gap: 10px; flex-wrap: wrap; justify-content: center; }
      #q3hud .menu .btnrow button { font-size: 15px; letter-spacing: 2px; padding: 10px 20px; }
      #q3hud .menu .withdraw { display: flex; flex-direction: column; gap: 10px; align-items: center; width: min(560px, 90vw); }
      #q3hud .menu .withdraw input { width: 100%; font: 14px ui-monospace, monospace; padding: 12px 14px; border-radius: 6px; border: 1px solid rgba(247,147,26,.5); background: rgba(10,10,15,.8); color: #e8e8e8; outline: none; }
      #q3hud .menu .withdraw input:focus { border-color: #f7931a; box-shadow: 0 0 12px rgba(247,147,26,.4); }
      #q3hud .menu .withdraw button { font-size: 18px; letter-spacing: 3px; padding: 10px 28px; }
      #q3hud .menu .ctl { font-family: ui-monospace, monospace; font-size: 12px; opacity: .55; text-align: center; line-height: 1.7; }
      /* --- classement « humains uniquement » --- */
      #q3hud .menu button.ghost { background: transparent; color: #e8e8e8; border: 2px solid rgba(247,147,26,.45); box-shadow: none; font-size: 16px; letter-spacing: 3px; padding: 9px 22px; }
      #q3hud .menu button.ghost:hover { background: rgba(247,147,26,.15); border-color: #f7931a; color: #ffb84d; }
      #q3hud .menu .lbtabs { display: flex; gap: 6px; flex-wrap: wrap; justify-content: center; }
      #q3hud .menu .lbtabs button { font: 12px/1 ui-monospace, monospace; letter-spacing: 2px; padding: 8px 14px; border-radius: 5px; background: rgba(20,20,30,.85); color: rgba(255,255,255,.65); border: 1px solid rgba(255,255,255,.14); box-shadow: none; }
      #q3hud .menu .lbtabs button:hover { color: #ffb84d; border-color: rgba(247,147,26,.6); background: rgba(247,147,26,.12); }
      #q3hud .menu .lbtabs button.sel { background: rgba(247,147,26,.18); border-color: #f7931a; color: #ffb84d; }
      /* écran dense : le classement ajoute 3 blocs, on resserre pour tenir en 1 vue mobile */
      #q3hud .menu.lb { gap: 12px; }
      #q3hud .menu .lbwrap { width: min(680px, 94vw); max-height: min(42vh, 400px); overflow-y: auto; -webkit-overflow-scrolling: touch; overscroll-behavior: contain; background: rgba(6,6,12,.88); border: 1px solid rgba(247,147,26,.45); border-radius: 8px; padding: 10px 12px; font-family: ui-monospace, monospace; font-size: 13px; }
      #q3hud .menu .lbwrap table { width: 100%; border-collapse: collapse; }
      #q3hud .menu .lbwrap th { position: sticky; top: -10px; background: #06060c; text-align: left; opacity: .55; font-weight: normal; font-size: 10px; letter-spacing: 1px; padding: 6px 6px; white-space: nowrap; }
      #q3hud .menu .lbwrap td { padding: 6px; border-top: 1px solid rgba(255,255,255,.07); white-space: nowrap; }
      #q3hud .menu .lbwrap td.r, #q3hud .menu .lbwrap th.r { text-align: right; }
      #q3hud .menu .lbwrap td.nm { color: #e8e8e8; max-width: 40vw; overflow: hidden; text-overflow: ellipsis; }
      #q3hud .menu .lbwrap td.st { color: #f7931a; }
      #q3hud .menu .lbwrap tr.me td { background: rgba(247,147,26,.16); color: #ffb84d; }
      #q3hud .menu .lbwrap tr.top1 td.rk { color: #ffd700; }
      #q3hud .menu .lbwrap .msg { padding: 22px 8px; text-align: center; opacity: .7; letter-spacing: 2px; line-height: 1.7; }
      #q3hud .menu .lbwrap .msg.err { color: #ff5a5a; opacity: .9; }
      @media (max-width: 560px) { #q3hud .menu .lbwrap { font-size: 12px; } #q3hud .menu .lbwrap .hide-s { display: none; } }
      /* touch */
      #q3touch { position: fixed; inset: 0; z-index: 950; touch-action: none; display: none; }
      #q3touch.on { display: block; }
      #q3touch .joy { position: absolute; width: 130px; height: 130px; border-radius: 50%; border: 2px solid rgba(247,147,26,.5); background: rgba(10,10,15,.35); display: none; }
      #q3touch .joy .knob { position: absolute; width: 56px; height: 56px; border-radius: 50%; left: 50%; top: 50%; transform: translate(-50%,-50%); background: rgba(247,147,26,.55); border: 2px solid rgba(255,184,77,.8); }
      #q3touch .btn { position: absolute; border-radius: 50%; display: flex; align-items: center; justify-content: center; color: #fff; font-size: 26px; background: rgba(247,147,26,.3); border: 2px solid rgba(247,147,26,.6); backdrop-filter: blur(2px); }
      #q3touch .btn:active { background: rgba(247,147,26,.6); }
      #q3touch .fire { width: 92px; height: 92px; right: 26px; bottom: 110px; font-size: 34px; }
      #q3touch .jump { width: 64px; height: 64px; right: 136px; bottom: 150px; }
      #q3touch .next { width: 52px; height: 52px; right: 30px; bottom: 220px; font-size: 18px; }
      #q3touch .zoom { width: 58px; height: 58px; right: 132px; bottom: 78px; font-size: 24px; }
      #q3touch .zoom.on { background: rgba(155,231,255,.55); border-color: rgba(155,231,255,.9); }
    `;
    document.head.appendChild(s);
  }

  // ---------------------------------------------------------------- menu

  showMenu(legends: Array<{ id: string; name: string; title: string; accent: number }>, isTouch: boolean): void {
    let selected = legends[0].id;
    this.menu.classList.remove('lb');
    this.menu.innerHTML = `
      <h1>LIGHTNING <span>⚡</span> ARENA</h1>
      <div class="sub">LIGHTNING YARD · FREE FOR ALL · ENTRÉE 2 100 SATS · 600 SATS / FRAG · 3 FRAGS PAYANTS MAX</div>
      <div class="legends">${legends.map((l, i) => `<div class="lg ${i === 0 ? 'sel' : ''}" data-id="${l.id}" style="--c:#${l.accent.toString(16).padStart(6, '0')}"><div class="sw"></div>${l.name}<div class="t">${l.title}</div></div>`).join('')}</div>
      <div class="nick">
        <label for="q3nick">PSEUDO</label>
        <input id="q3nick" type="text" maxlength="${NICK_MAX}" placeholder="PLAYER" autocomplete="off" autocapitalize="characters" autocorrect="off" spellcheck="false" enterkeyhint="go" />
        <span class="cnt" id="q3nickcnt"></span>
      </div>
      <button id="q3start">FIGHT — GRATUIT</button>
      <button id="q3join" class="pay" style="display:none">⚡ ENTRER — 2 100 SATS · RÉCUPÈRE JUSQU'À 1 800</button>
      <button id="q3leaderboard" class="ghost">🏆 CLASSEMENT ⚡</button>
      <div class="ctl">${isTouch ? 'LEFT: MOVE · RIGHT: LOOK · ⚡ FIRE · ⤒ JUMP · ⇄ WEAPON' : 'WASD MOVE · MOUSE LOOK · LMB FIRE · SPACE JUMP · 1-4 / WHEEL WEAPON · TAB SCORES · STRAFE-JUMP FOR SPEED'}</div>
    `;
    this.menu.classList.remove('hide');
    this.menu.querySelectorAll<HTMLElement>('.lg').forEach(el => {
      el.addEventListener('click', () => {
        this.menu.querySelectorAll('.lg').forEach(e => e.classList.remove('sel'));
        el.classList.add('sel');
        selected = el.dataset.id!;
      });
    });
    this.setupNickInput();
    this.menu.querySelector('#q3start')!.addEventListener('click', () => this.startFight(selected));
    this.menu.querySelector('#q3join')!.addEventListener('click', () => {
      this.commitNickname();
      this.menu.classList.add('hide');
      this.onJoinArena?.();
    });
    // le pseudo saisi est mémorisé avant de quitter le menu : au retour du
    // classement, `showMenu()` le repré-remplit depuis `this.nickname`
    this.menu.querySelector('#q3leaderboard')!.addEventListener('click', () => {
      this.commitNickname();
      this.showLeaderboard();
    });
  }

  /**
   * Champ pseudo : pré-rempli depuis localStorage, filtré à la frappe (whitelist
   * `[A-Za-z0-9_- ]`, 14 caractères) et mémorisé à l'entrée en arène.
   */
  private setupNickInput(): void {
    const input = this.menu.querySelector<HTMLInputElement>('#q3nick');
    const cnt = this.menu.querySelector<HTMLElement>('#q3nickcnt');
    if (!input) return;
    input.value = this.nickname;
    const sync = (): void => {
      const clean = filterNicknameInput(input.value);
      if (clean !== input.value) input.value = clean;
      this.nickname = clean;
      if (cnt) cnt.textContent = `${clean.length}/${NICK_MAX}`;
    };
    sync();
    input.addEventListener('input', sync);
    input.addEventListener('keydown', (e) => {
      if (e.key !== 'Enter') return;
      e.preventDefault();
      input.blur();
      (this.menu.querySelector('#q3start') as HTMLElement | null)?.click();
    });
  }

  /** Valide + persiste le pseudo (fallback silencieux si vide/invalide). */
  private commitNickname(): string {
    this.nickname = saveNickname(this.nickname);
    return this.nickname;
  }

  /** Pseudo affiché pour le joueur local (toujours assaini). */
  getNickname(): string {
    return this.nickname;
  }

  /** Wire-up: start fight with the given legend (called by FIGHT or after payment). */
  startFight(legendId?: string): void {
    let id = legendId;
    if (!id) {
      const sel = this.menu.querySelector<HTMLElement>('.lg.sel');
      id = sel?.dataset.id ?? undefined;
    }
    this.menu.classList.add('hide');
    this.pollStop?.();
    this.onStart?.(id ?? 'adam-back', this.commitNickname());
  }

  /** Shows the paid-entry button only when the arena API is reachable. */
  setArenaAvailable(ok: boolean): void {
    const b = this.menu.querySelector('#q3join') as HTMLElement | null;
    if (b) b.style.display = ok ? '' : 'none';
    // le classement est servi par le même hôte : injoignable ⇒ message « hors ligne »
    this.lbOnline = ok;
  }

  /** Refresh the economy numbers from the server. */
  setArenaEconomy(entrySats: number, bountySats: number, maxFrags: number): void {
    const b = this.menu.querySelector('#q3join') as HTMLElement | null;
    if (b) b.textContent = `⚡ ENTRER — ${entrySats.toLocaleString('fr-FR')} SATS · RÉCUPÈRE JUSQU'À ${(bountySats * maxFrags).toLocaleString('fr-FR')}`;
    const sub = this.menu.querySelector('.sub');
    if (sub) sub.textContent = `LIGHTNING YARD · FREE FOR ALL · ENTRÉE ${entrySats.toLocaleString('fr-FR')} SATS · ${bountySats} SATS / FRAG · ${maxFrags} FRAGS PAYANTS MAX`;
  }

  /** Full-screen payment panel: QR + lightning: link + copy + cancel. */
  async showJoinPayment(session: ArenaJoin, qrUrl: string): Promise<void> {
    this.menu.innerHTML = `
      <h1>ENTRÉE <span>⚡</span> ${session.entry_sats.toLocaleString('fr-FR')} SATS</h1>
      <div class="sub">PAYE L'INVOICE POUR ACTIVER LE BOUNTY RÉEL — ${session.bounty_sats.toLocaleString('fr-FR')} SATS / FRAG</div>
      <img class="qr" src="${qrUrl}" alt="QR code de paiement Lightning" />
      <div class="invoice" id="q3invoice">${session.payment_request}</div>
      <div class="btnrow">
        <button id="q3copy">Copier</button>
        <button id="q3lightning">Ouvrir le wallet ⚡</button>
        <button id="q3cancel">Annuler</button>
      </div>
      <div class="sub" id="q3paystate">En attente du paiement… (sondage automatique)</div>
    `;
    this.menu.classList.remove('hide');
    this.menu.querySelector('#q3copy')!.addEventListener('click', async () => {
      try { await navigator.clipboard.writeText(session.payment_request); this.frag('INVOICE COPIÉE', 'colle-la dans ton wallet Lightning'); }
      catch { this.frag('COPIE MANUELLE', 'sélectionne le texte ci-dessus'); }
    });
    this.menu.querySelector('#q3lightning')!.addEventListener('click', () => {
      window.location.href = 'lightning:' + session.payment_request;
    });
    this.menu.querySelector('#q3cancel')!.addEventListener('click', () => {
      this.pollStop?.();
      this.onCancelJoin?.();
      this.menu.classList.add('hide');
      this.backToMenu();
    });
  }

  /** Simple "back to menu" used after cancel — reshow the legend select. */
  backToMenu(): void {
    const btn = document.createElement('button');
    btn.textContent = 'Retour au menu';
    // pas de location.reload() : recharger perdait la session Lightning payée
    btn.addEventListener('click', () => this.onBackToMenu?.());
    this.menu.innerHTML = `<h1>LIGHTNING <span>⚡</span> ARENA</h1><div class="sub">PAIEMENT ANNULÉ</div>`;
    this.menu.appendChild(btn);
    this.menu.classList.remove('hide');
  }

  /** Called when the entry payment settles. */
  setArenaPaid(paid: boolean, pool: number): void {
    this.frag('ENTRÉE PAYÉE ⚡', `BOUNTY RÉEL ACTIVÉ · TRÉSORERIE ${pool.toLocaleString('fr-FR')} SATS`);
    const st = this.menu.querySelector('#q3paystate');
    if (st) st.textContent = paid ? '✓ Payé — le combat démarre !' : 'En attente du paiement…';
  }

  /** End screen: withdraw box (LNURL QR) when a paid session exists. */
  showMenuEnd(rows: ScoreRow[], won: boolean, satsEarned: number, arena?: { paid: boolean; credit: number; pool: number }): void {
    this.menu.innerHTML = `
      <h1>${won ? 'YOU <span>WIN</span>' : 'MATCH <span>OVER</span>'}</h1>
      <div class="sub">${won ? 'FRAG LIMIT HIT — THE THRONE IS YOURS' : 'BETTER LUCK NEXT BLOCK'}</div>
      <div class="sub" style="color:#f7931a;font-size:18px">⚡ ${satsEarned.toLocaleString('fr-FR')} SATS EARNED</div>
      <div class="scoreboard show" style="position:static;transform:none">${this.scoreTable(rows)}</div>
      ${arena && arena.paid ? `
      <div class="withdraw">
        <div class="sub" style="color:#f7931a">CRÉDIT BOUNTY : ${arena.credit.toLocaleString('fr-FR')} SATS · POOL : ${arena.pool.toLocaleString('fr-FR')} SATS</div>
        <button id="q3withdrawbtn">⚡ GÉNÉRER LE RETRAIT LNURL</button>
        <div class="sub" id="q3withdrawstate"></div>
        <img id="q3withdrawqr" alt="QR" style="display:none;width:min(440px,88vw);height:auto;border-radius:10px;background:#fff;padding:10px;margin:6px auto" />
      </div>` : (arena && !arena.paid ? `<div class="sub">BOUNTY RÉEL : entre avec 2 100 sats pour récupérer jusqu'à 1 800</div>` : '')}
      <button id="q3again">PLAY AGAIN</button>
    `;
    this.menu.classList.remove('hide');
    // PLAY AGAIN : on revient au menu en mémoire, la session payée reste active
    this.menu.querySelector('#q3again')!.addEventListener('click', () => this.onBackToMenu?.());
    const wb = this.menu.querySelector('#q3withdrawbtn') as HTMLElement | null;
    if (wb) {
      wb.addEventListener('click', () => {
        wb.setAttribute('disabled', 'true');
        this.onWithdraw?.();
      });
    }
  }

  /**
   * Session payée retrouvée : le bouton d'entrée devient « bounty actif » (on ne
   * repaie pas une entrée déjà réglée).
   */
  setArenaRestored(credit: number, pool: number): void {
    const b = this.menu.querySelector('#q3join') as HTMLElement | null;
    if (b) {
      b.textContent = `⚡ ENTRÉE DÉJÀ PAYÉE — BOUNTY ACTIF · CRÉDIT ${credit.toLocaleString('fr-FR')} SATS`;
      b.setAttribute('disabled', 'true');
      b.style.opacity = '0.75';
    }
    const sub = this.menu.querySelector('.sub');
    if (sub) sub.textContent = `SESSION ⚡ ACTIVE · POOL ${pool.toLocaleString('fr-FR')} SATS`;
  }

  /**
   * Crédit bounty retrouvé au démarrage (session Lightning déjà payée lors d'une
   * session précédente) : on propose le retrait sans relancer de match.
   */
  showPendingCredit(credit: number, pool: number): void {
    const box = document.createElement('div');
    box.className = 'withdraw';
    box.innerHTML = `
      <div class="sub" style="color:#f7931a">SESSION ⚡ RETROUVÉE — CRÉDIT ${credit.toLocaleString('fr-FR')} SATS · POOL ${pool.toLocaleString('fr-FR')} SATS</div>
      <button id="q3withdrawbtn">⚡ GÉNÉRER LE RETRAIT LNURL</button>
      <div class="sub" id="q3withdrawstate"></div>
      <img id="q3withdrawqr" alt="QR" style="display:none;width:min(440px,88vw);height:auto;border-radius:10px;background:#fff;padding:10px;margin:6px auto" />
    `;
    const start = this.menu.querySelector('#q3start');
    if (start) this.menu.insertBefore(box, start); else this.menu.appendChild(box);
    this.menu.classList.remove('hide');
    box.querySelector('#q3withdrawbtn')!.addEventListener('click', (e) => {
      (e.currentTarget as HTMLElement).setAttribute('disabled', 'true');
      this.onWithdraw?.();
    });
  }

  /** Show the LNURL-withdraw QR for the player to scan with their wallet. */
  showWithdrawQr(amountSats: number): void {
    const img = this.menu.querySelector('#q3withdrawqr') as HTMLImageElement | null;
    const st = this.menu.querySelector('#q3withdrawstate') as HTMLElement | null;
    if (img && this.arenaQrUrl) {
      img.src = this.arenaQrUrl;
      img.style.display = 'block';
    }
    if (st) {
      st.textContent = `Scanne le QR avec ton wallet pour encaisser ${amountSats.toLocaleString('fr-FR')} sats ⚡`;
      st.style.color = '#f7931a';
    }
  }

  showWithdrawClaimed(): void {
    const st = this.menu.querySelector('#q3withdrawstate') as HTMLElement | null;
    const img = this.menu.querySelector('#q3withdrawqr') as HTMLImageElement | null;
    if (img) img.style.display = 'none';
    if (st) {
      st.textContent = '✓ BOUNTY ENCAISSÉ ⚡ — SÉCURISÉ SUR TON WALLET';
      st.style.color = '#22c55e';
    }
    this.frag('BOUNTY PAYÉ ⚡', 'LIGHTNING SETTLED — GG');
  }

  showWithdrawResult(ok: boolean, error?: string): void {
    const st = this.menu.querySelector('#q3withdrawstate') as HTMLElement | null;
    const wb = this.menu.querySelector('#q3withdrawbtn') as HTMLElement | null;
    if (wb) wb.removeAttribute('disabled');
    if (st) {
      st.textContent = ok ? '✓ RETRAIT CRÉÉ' : `✗ ${error ?? 'échec'}`;
      st.style.color = ok ? '#22c55e' : '#ff5a5a';
    }
  }

  /** Set the QR URL for the withdraw LNURL (session token based). */
  setWithdrawQrUrl(url: string | null): void {
    this.arenaQrUrl = url;
  }

  // ------------------------------------------------------------ classement

  /**
   * Identité de classement du joueur local (sha256 de sa session payée) —
   * sert UNIQUEMENT à surligner sa ligne et à demander son rang. `null` en
   * démo/hors ligne : l'écran reste parfaitement utilisable sans.
   */
  setLeaderboardIdentity(playerId: string | null): void {
    this.lbPlayerId = playerId;
  }

  /**
   * Écran CLASSEMENT : top des joueurs HUMAINS (le serveur n'indexe que des
   * sessions Lightning payées — aucun bot ne peut y figurer, et l'UI n'affiche
   * strictement que ce que le serveur renvoie).
   */
  showLeaderboard(): void {
    this.menu.innerHTML = `
      <h1>CLASSEMENT <span>⚡</span></h1>
      <div class="sub">JOUEURS HUMAINS · SESSIONS LIGHTNING PAYÉES · LES BOTS NE SONT JAMAIS CLASSÉS</div>
      <div class="lbtabs" id="q3lbtabs">
        <button type="button" data-p="alltime">TOUT TEMPS</button>
        <button type="button" data-p="weekly">CETTE SEMAINE</button>
        <button type="button" data-p="daily">AUJOURD'HUI</button>
      </div>
      <div class="lbwrap" id="q3lbbody"></div>
      <div class="sub" id="q3lbrank"></div>
      <div class="btnrow"><button id="q3lbback">← RETOUR</button></div>
    `;
    this.menu.classList.add('lb');
    this.menu.classList.remove('hide');
    this.menu.querySelectorAll<HTMLElement>('#q3lbtabs button').forEach(el => {
      el.addEventListener('click', () => {
        const p = el.dataset.p as LeaderboardPeriod;
        if (p === this.lbPeriod) return;
        this.lbPeriod = p;
        void this.loadLeaderboard();
      });
    });
    this.menu.querySelector('#q3lbback')!.addEventListener('click', () => this.onBackToMenu?.());
    void this.loadLeaderboard();
  }

  /** Message plein cadre (chargement / erreur / vide). */
  private lbMessage(text: string, error = false): void {
    const body = this.menu.querySelector<HTMLElement>('#q3lbbody');
    if (!body) return;
    body.textContent = '';
    const d = document.createElement('div');
    d.className = error ? 'msg err' : 'msg';
    d.textContent = text;
    body.appendChild(d);
  }

  private async loadLeaderboard(): Promise<void> {
    this.menu.querySelectorAll<HTMLElement>('#q3lbtabs button').forEach(el => {
      el.classList.toggle('sel', el.dataset.p === this.lbPeriod);
    });
    const rankLine = this.menu.querySelector<HTMLElement>('#q3lbrank');
    if (rankLine) rankLine.textContent = '';
    // jeton de requête : un aller-retour lent sur « tout temps » ne doit pas
    // écraser l'affichage d'un onglet cliqué entre-temps
    const req = ++this.lbReq;
    if (!this.onLoadLeaderboard) { this.lbMessage('CLASSEMENT INDISPONIBLE HORS LIGNE'); return; }
    this.lbMessage('CHARGEMENT…');
    let entries: LeaderboardEntry[];
    try {
      entries = await this.onLoadLeaderboard(this.lbPeriod);
    } catch {
      if (req === this.lbReq) {
        this.lbMessage(this.lbOnline
          ? 'CLASSEMENT INDISPONIBLE — RÉESSAIE PLUS TARD'
          : 'CLASSEMENT INDISPONIBLE HORS LIGNE — LE JEU RESTE JOUABLE EN DÉMO', true);
      }
      return;
    }
    if (req !== this.lbReq) return;
    if (!entries.length) {
      this.lbMessage(this.lbPeriod === 'alltime'
        ? 'AUCUN JOUEUR CLASSÉ POUR L’INSTANT — ENTRE EN ARÈNE ⚡'
        : 'PERSONNE SUR CETTE PÉRIODE — SOIS LE PREMIER ⚡');
    } else {
      this.renderLeaderboard(entries);
    }
    void this.loadMyRank(req, entries);
  }

  /** Rang hors top : demandé seulement si le joueur a une session payée. */
  private async loadMyRank(req: number, entries: LeaderboardEntry[]): Promise<void> {
    const line = this.menu.querySelector<HTMLElement>('#q3lbrank');
    if (!line || !this.lbPlayerId || !this.onLoadMyRank) return;
    if (entries.some(e => e.pubkey === this.lbPlayerId)) return;
    let rank = 0;
    try { rank = await this.onLoadMyRank(this.lbPeriod); } catch { return; }
    if (req !== this.lbReq) return;
    line.style.color = '#f7931a';
    line.textContent = rank > 0 ? `TON RANG : #${rank}` : 'TU N’ES PAS ENCORE CLASSÉ SUR CETTE PÉRIODE';
  }

  /**
   * Table du classement — construite en DOM pur : `name` vient du serveur (déjà
   * assaini) mais n'est JAMAIS injecté en innerHTML.
   */
  private renderLeaderboard(entries: LeaderboardEntry[]): void {
    const body = this.menu.querySelector<HTMLElement>('#q3lbbody');
    if (!body) return;
    const table = document.createElement('table');
    const head = document.createElement('tr');
    for (const [label, cls] of [['#', 'rk'], ['JOUEUR', 'nm'], ['KILLS', 'r'], ['⚡ SATS', 'r'], ['ELO', 'r hide-s'], ['K/D', 'r hide-s']] as const) {
      const th = document.createElement('th');
      th.className = cls;
      th.textContent = label;
      head.appendChild(th);
    }
    table.appendChild(head);

    entries.forEach((e, i) => {
      const tr = document.createElement('tr');
      const rank = e.rank > 0 ? e.rank : i + 1;
      if (this.lbPlayerId && e.pubkey === this.lbPlayerId) tr.classList.add('me');
      if (rank === 1) tr.classList.add('top1');
      const cells: Array<[string, string]> = [
        [String(rank), 'rk'],
        [e.name + (this.lbPlayerId && e.pubkey === this.lbPlayerId ? ' (toi)' : ''), 'nm'],
        [String(Math.round(e.kills)), 'r'],
        [Math.round(e.sats).toLocaleString('fr-FR'), 'r st'],
        [String(Math.round(e.elo)), 'r hide-s'],
        [e.kdRatio.toFixed(2), 'r hide-s'],
      ];
      for (const [text, cls] of cells) {
        const td = document.createElement('td');
        td.className = cls;
        td.textContent = text;
        tr.appendChild(td);
      }
      table.appendChild(tr);
    });

    body.textContent = '';
    body.appendChild(table);
    body.scrollTop = 0;
  }

  // ---------------------------------------------------------------- updates

  private buildWeaponBar(): void {
    this.els['hud-weapons'].innerHTML = WEAPON_ORDER.map((id, i) => `<div class="w" data-w="${id}"><span>${i + 1}</span><small>${WEAPONS[id].short}</small></div>`).join('');
  }

  setStats(health: number, armor: number, ammo: Record<WeaponId, number>, weapon: WeaponId, sats: number, frags: number, lead: string, speed: number, quad: boolean): void {
    // Toutes les écritures DOM sont conditionnées au changement de valeur :
    // à 60 fps une réécriture systématique déclenchait un reflow par frame.
    const L = this.last;
    const hp = Math.max(0, Math.ceil(health));
    if (hp !== L.health) {
      L.health = hp;
      this.els['hud-health'].textContent = String(hp);
      this.els['hud-health'].parentElement!.classList.toggle('low', hp <= LOW_HP);
    }
    const ar = Math.max(0, Math.ceil(armor));
    if (ar !== L.armor) { L.armor = ar; this.els['hud-armor'].textContent = String(ar); }
    const am = ammo[weapon];
    if (am !== L.ammo) { L.ammo = am; this.els['hud-ammo'].textContent = String(am); }
    if (weapon !== L.weapon) {
      L.weapon = weapon;
      this.els['hud-weapon'].textContent = WEAPONS[weapon].name.toUpperCase();
    }
    this.setCrosshair(weapon);
    this.setLowHealth(hp > 0 && hp <= LOW_HP);
    if (sats !== L.sats) { L.sats = sats; this.els['hud-sats'].textContent = sats.toLocaleString('fr-FR'); }
    if (frags !== L.frags) { L.frags = frags; this.els['hud-frags'].textContent = String(frags); }
    if (lead !== L.lead) { L.lead = lead; this.els['hud-lead'].textContent = lead; }
    const ups = Math.round(speed * 33.7);
    if (ups !== L.ups) { L.ups = ups; this.speedVal.textContent = String(ups); }
    if (quad !== L.quad) { L.quad = quad; this.els['hud-quad'].style.display = quad ? 'block' : 'none'; }
    // signature « arme sélectionnée + qui est à sec » : la barre n'est réécrite
    // que si l'un des deux change (et non 60 fois par seconde)
    let mask = 0;
    for (let i = 0; i < WEAPON_ORDER.length; i++) if (ammo[WEAPON_ORDER[i]] <= 0) mask |= 1 << i;
    if (weapon !== L.barWeapon || mask !== L.barMask) {
      L.barWeapon = weapon; L.barMask = mask;
      this.els['hud-weapons'].querySelectorAll<HTMLElement>('.w').forEach(el => {
        const id = el.dataset.w as WeaponId;
        el.classList.toggle('sel', id === weapon);
        el.classList.toggle('empty', ammo[id] <= 0);
      });
    }
  }

  setTime(seconds: number): void {
    const m = Math.floor(seconds / 60), s = Math.floor(seconds % 60);
    this.els['hud-time'].textContent = `${m}:${s.toString().padStart(2, '0')}`;
  }

  setNet(text: string, ok: boolean): void {
    this.els['hud-net'].textContent = `● ${text}`;
    this.els['hud-net'].style.color = ok ? '#22c55e' : '#ff5a5a';
  }

  /**
   * Hitmarker gradué : la longueur des branches suit les dégâts, un kill
   * ajoute l'anneau orange. Légère rotation aléatoire pour la lisibilité.
   */
  hitmarker(amount = 10, kill = false): void {
    const x = this.xhair;
    const hm = x.querySelector<HTMLElement>('.hm')!;
    const len = 6 + Math.min(14, amount * 0.16);
    hm.style.setProperty('--len', `${len.toFixed(1)}px`);
    hm.style.transform = `rotate(${(Math.random() - 0.5) * 12}deg)`;
    x.classList.remove('kill');
    x.classList.remove('hit');
    // pas de `void x.offsetWidth` (reflow synchrone) : l'animation redémarre
    // à la frame suivante, imperceptible à 60 fps.
    requestAnimationFrame(() => { x.classList.toggle('kill', kill); x.classList.add('hit'); });
    if (kill) this.damageFlashColor('#ffb84d', 0.25);
    clearTimeout(this.hitmarkerT);
    this.hitmarkerT = window.setTimeout(() => { x.classList.remove('hit'); x.classList.remove('kill'); }, kill ? 260 : 110);
  }

  /** Réticule dédié à l'arme courante. */
  setCrosshair(weapon: WeaponId): void {
    if (this.xhairWeapon === weapon) return;
    this.xhairWeapon = weapon;
    this.xhair.className = `xhair w-${weapon}` + (this.aimEnemy ? ' enemy' : '');
  }

  /**
   * Écartement dynamique : +6 px à pleine vitesse, +4 px en l'air,
   * retour élastique au sol (lissage exponentiel).
   */
  setSpread(speed: number, airborne: boolean, dt: number): void {
    const target = 6 + Math.min(6, speed * 0.55) + (airborne ? 4 : 0);
    this.spread += (target - this.spread) * Math.min(1, dt * (target > this.spread ? 14 : 7));
    this.xhair.style.setProperty('--sp', `${this.spread.toFixed(2)}px`);
  }

  /** Réticule rouge + léger agrandissement quand la visée est sur un ennemi. */
  setAimOnEnemy(on: boolean): void {
    if (this.aimEnemy === on) return;
    this.aimEnemy = on;
    this.xhair.classList.toggle('enemy', on);
  }

  /** Coupe le zoom tactile ET l'état visuel du bouton ⊙ (mort, respawn, fin). */
  setTouchZoomOff(): void {
    this.touch.zoom = false;
    this.zoomBtn?.classList.remove('on');
  }

  /** Overlay lunette (la mécanique de FOV est côté jeu). */
  setZooming(on: boolean): void {
    if (this.zooming === on) return;
    this.zooming = on;
    this.els['hud-scope'].classList.toggle('on', on);
    this.xhair.style.opacity = on ? '0.35' : '1';
  }

  /** Vignette rouge pulsée sous LOW_HP PV. */
  setLowHealth(on: boolean): void {
    if (this.lowHp === on) return;
    this.lowHp = on;
    this.flash.classList.toggle('lowhp', on);
    if (!on) this.flash.style.opacity = '0';
  }

  /**
   * Arc directionnel de dégâts. `angleRad` = angle du tireur relatif à la
   * caméra (0 = devant, positif = à droite), tourné autour du réticule.
   */
  damageFrom(angleRad: number): void {
    // pool de 4 arcs réutilisés : sous feu nourri on créait un élément par tick
    if (this.dmgArcs.length < 4) {
      const el = document.createElement('div');
      el.className = 'arc';
      this.els['hud-dmgdir'].appendChild(el);
      this.dmgArcs.push(el);
    }
    const el = this.dmgArcs[this.dmgArcAt % this.dmgArcs.length];
    this.dmgArcAt++;
    el.style.animation = 'none';
    el.style.transform = `rotate(${(angleRad * 180 / Math.PI).toFixed(1)}deg)`;
    requestAnimationFrame(() => { el.style.animation = ''; });
  }

  /** Flash de vignette teinté (kill / pickup). */
  private damageFlashColor(color: string, intensity: number): void {
    if (this.lowHp) return;
    this.flash.style.background = `radial-gradient(ellipse at center, rgba(0,0,0,0) 40%, ${color} 100%)`;
    this.flash.style.opacity = String(intensity);
    setTimeout(() => {
      this.flash.style.opacity = '0';
      this.flash.style.background = '';
    }, 130);
  }

  damageFlash(intensity = 1): void {
    this.flash.style.opacity = String(Math.min(1, intensity));
    setTimeout(() => { this.flash.style.opacity = '0'; }, 120);
  }

  /** Nombre maximum de lignes simultanées dans le kill feed. */
  private static readonly KILLFEED_MAX = 8;

  kill(killer: string, victim: string, weapon: string, sats?: number): void {
    const el = document.createElement('div');
    el.className = 'k';
    // construction par nœuds : `killer`/`victim` peuvent être le pseudo joueur
    const b = document.createElement('b');
    b.textContent = killer;
    el.append(b, ` ⚡ ${victim} `);
    const w = document.createElement('span');
    w.style.opacity = '.6';
    w.textContent = `[${weapon}]`;
    el.appendChild(w);
    if (sats) {
      const s = document.createElement('span');
      s.style.color = '#f7931a';
      s.textContent = ` +${sats}`;
      el.appendChild(s);
    }
    this.killfeed.appendChild(el);
    // purge immédiate des plus anciennes : lors d'un carnage (splash RL sur 3
    // bots) le conteneur pouvait empiler des dizaines de lignes animées avant
    // que leurs timers de 4 s ne les retirent.
    while (this.killfeed.childElementCount > HUD.KILLFEED_MAX) this.killfeed.firstElementChild!.remove();
    setTimeout(() => el.remove(), 4000);
  }

  private fragT = 0;
  frag(text: string, sub?: string): void {
    // textContent : `text`/`sub` embarquent le pseudo joueur sur certains messages
    this.fragMsg.textContent = text;
    if (sub) {
      const d = document.createElement('div');
      d.className = 'sub';
      d.textContent = sub;
      this.fragMsg.appendChild(d);
    }
    this.fragMsg.classList.add('show');
    clearTimeout(this.fragT);
    this.fragT = window.setTimeout(() => this.fragMsg.classList.remove('show'), 2200);
  }

  scoreTable(rows: ScoreRow[]): string {
    const sorted = [...rows].sort((a, b) => b.frags - a.frags || a.deaths - b.deaths);
    return `<h2>SCOREBOARD</h2><table><tr><th>#</th><th>LEGEND</th><th class="r">FRAGS</th><th class="r">DEATHS</th><th class="r">⚡ SATS</th></tr>
      ${sorted.map((r, i) => `<tr class="${r.me ? 'me' : ''}"><td>${i + 1}</td><td style="color:${r.color}">${esc(r.name)}${r.me ? ' (you)' : ''}${r.legend ? ` <span class="lg">· ${esc(r.legend)}</span>` : ''}</td><td class="r">${r.frags}</td><td class="r">${r.deaths}</td><td class="r" style="color:#f7931a">${r.sats.toLocaleString('fr-FR')}</td></tr>`).join('')}
    </table>`;
  }

  showScoreboard(rows: ScoreRow[] | null): void {
    if (!rows) { this.scoreboard.classList.remove('show'); return; }
    this.scoreboard.innerHTML = this.scoreTable(rows);
    this.scoreboard.classList.add('show');
  }

  // ---------------------------------------------------------------- touch

  private setupTouch(): void {
    const isTouch = ('ontouchstart' in window) || navigator.maxTouchPoints > 0;
    if (!isTouch) return;
    this.touch.active = true;
    const t = document.createElement('div');
    t.id = 'q3touch';
    t.innerHTML = `<div class="joy"><div class="knob"></div></div><div class="btn fire">⚡</div><div class="btn jump">⤒</div><div class="btn next">⇄</div><div class="btn zoom">⊙</div>`;
    document.body.appendChild(t);
    const joy = t.querySelector<HTMLElement>('.joy')!, knob = t.querySelector<HTMLElement>('.knob')!;
    const fire = t.querySelector<HTMLElement>('.fire')!, jump = t.querySelector<HTMLElement>('.jump')!, next = t.querySelector<HTMLElement>('.next')!;
    const zoom = this.zoomBtn = t.querySelector<HTMLElement>('.zoom')!;

    t.addEventListener('touchstart', (e) => {
      for (const p of Array.from(e.changedTouches)) {
        const target = document.elementFromPoint(p.clientX, p.clientY);
        if (target && (target as HTMLElement).closest('.btn')) continue;
        if (p.clientX < window.innerWidth / 2 && this.joyId === -1) {
          this.joyId = p.identifier; this.joyOrigin = { x: p.clientX, y: p.clientY };
          joy.style.display = 'block'; joy.style.left = `${p.clientX - 65}px`; joy.style.top = `${p.clientY - 65}px`;
          knob.style.transform = 'translate(-50%,-50%)';
        } else if (p.clientX >= window.innerWidth / 2 && this.lookId === -1) {
          this.lookId = p.identifier; this.lookLast = { x: p.clientX, y: p.clientY };
        }
      }
      e.preventDefault();
    }, { passive: false });
    t.addEventListener('touchmove', (e) => {
      for (const p of Array.from(e.changedTouches)) {
        if (p.identifier === this.joyId) {
          let dx = p.clientX - this.joyOrigin.x, dy = p.clientY - this.joyOrigin.y;
          const l = Math.hypot(dx, dy), m = 55;
          if (l > m) { dx = dx / l * m; dy = dy / l * m; }
          knob.style.transform = `translate(calc(-50% + ${dx}px), calc(-50% + ${dy}px))`;
          this.touch.moveX = dx / m; this.touch.moveY = -dy / m;
        } else if (p.identifier === this.lookId) {
          this.touch.lookDX += p.clientX - this.lookLast.x; this.touch.lookDY += p.clientY - this.lookLast.y;
          this.lookLast = { x: p.clientX, y: p.clientY };
        }
      }
      e.preventDefault();
    }, { passive: false });
    const end = (e: TouchEvent) => {
      for (const p of Array.from(e.changedTouches)) {
        if (p.identifier === this.joyId) { this.joyId = -1; this.touch.moveX = 0; this.touch.moveY = 0; joy.style.display = 'none'; }
        if (p.identifier === this.lookId) this.lookId = -1;
      }
    };
    t.addEventListener('touchend', end); t.addEventListener('touchcancel', end);
    const press = (fn: () => void) => (e: Event) => { e.preventDefault(); e.stopPropagation(); fn(); };
    fire.addEventListener('touchstart', press(() => { this.touch.fire = true; }), { passive: false });
    fire.addEventListener('touchend', press(() => { this.touch.fire = false; }), { passive: false });
    fire.addEventListener('touchcancel', press(() => { this.touch.fire = false; }), { passive: false });
    jump.addEventListener('touchstart', press(() => { this.touch.jump = true; }), { passive: false });
    jump.addEventListener('touchend', press(() => { this.touch.jump = false; }), { passive: false });
    next.addEventListener('touchstart', press(() => { this.onNextWeapon?.(); }), { passive: false });
    // zoom tactile : bascule (maintien peu pratique au pouce)
    zoom.addEventListener('touchstart', press(() => { this.touch.zoom = !this.touch.zoom; zoom.classList.toggle('on', this.touch.zoom); }), { passive: false });
  }
  public onNextWeapon: (() => void) | null = null;

  setTouchVisible(v: boolean): void {
    document.getElementById('q3touch')?.classList.toggle('on', v && this.touch.active);
  }

  /** consume accumulated look delta */
  takeLook(): { dx: number; dy: number } {
    const r = { dx: this.touch.lookDX, dy: this.touch.lookDY };
    this.touch.lookDX = 0; this.touch.lookDY = 0;
    return r;
  }
}